import React from 'react';

export interface CACaptionData {
  attorneyBlock: string;
  courtName: string;
  countyName: string;
  plaintiff: string;
  defendant: string;
  caseNumber: string;
  dept: string;
  judge: string;
  title: string;
  hearingDate: string;
  hearingTime: string;
  hearingLocation: string;
}

export interface CACaptionComponentProps extends CACaptionData {
  readOnly?: boolean;
  onChange?: (data: Partial<CACaptionData>) => void;
  additionalParties?: string[];
  alignFields?: boolean;
  onFocusTitle?: () => void;
  onBlurTitle?: () => void;
  titleBold?: boolean;
  titleItalic?: boolean;
}

const Row: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-1 whitespace-pre-wrap break-words">{children}</div>
);

export const CACaptionComponent: React.FC<CACaptionComponentProps> = ({
  attorneyBlock,
  courtName,
  countyName,
  plaintiff,
  defendant,
  caseNumber,
  dept,
  judge,
  title,
  hearingDate,
  hearingTime,
  hearingLocation,
  readOnly = false,
  onChange,
  additionalParties = [],
  alignFields = true,
  onFocusTitle,
  onBlurTitle,
  titleBold,
  titleItalic,
}) => {
  const inputCls = 'w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white';
  const labelFor = (idx: number) => (idx === 0 ? 'Intervenor' : idx === 1 ? 'Cross-Defendant' : `Additional Party ${idx + 1}`);

  return (
    <div className="ca-caption-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 260px', columnGap: 24 }}>
      <div className="ca-caption-left text-sm">
        <Row>
          {readOnly ? (
            attorneyBlock
          ) : (
            <textarea className={inputCls} rows={4} value={attorneyBlock} onChange={(e) => onChange?.({ attorneyBlock: e.target.value })} />
          )}
        </Row>
        <Row>
          {readOnly ? (
            <>{courtName}, {countyName}</>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <input className={inputCls} value={courtName} onChange={(e) => onChange?.({ courtName: e.target.value })} />
              <input className={inputCls} value={countyName} onChange={(e) => onChange?.({ countyName: e.target.value })} />
            </div>
          )}
        </Row>
        <Row>
          {readOnly ? (
            <>
              {plaintiff} v. {defendant}
            </>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <input className={inputCls} value={plaintiff} onChange={(e) => onChange?.({ plaintiff: e.target.value })} />
              <input className={inputCls} value={defendant} onChange={(e) => onChange?.({ defendant: e.target.value })} />
            </div>
          )}
        </Row>
        {additionalParties.map((p, idx) => (
          <Row key={idx}>
            {readOnly ? (
              <>{labelFor(idx)}: {p}</>
            ) : (
              <div className="grid grid-cols-3 gap-2 items-center">
                <div className="text-xs text-gray-500">{labelFor(idx)}:</div>
                <input className={inputCls + ' col-span-2'} value={p} onChange={(e) => {
                  const np = [...additionalParties];
                  np[idx] = e.target.value;
                  onChange?.({} as any); // trigger update through node-level handler
                }} />
              </div>
            )}
          </Row>
        ))}
      </div>
      <div className="ca-caption-right text-sm">
        <Row>
          {readOnly ? (
            <>Case No.: {caseNumber} | Dept: {dept} | Judge: {judge}</>
          ) : (
            <div className="grid grid-cols-3 gap-2" style={{ gridTemplateColumns: alignFields ? '110px 70px 1fr' : undefined }}>
              <input className={inputCls} value={caseNumber} onChange={(e) => onChange?.({ caseNumber: e.target.value })} />
              <input className={inputCls} value={dept} onChange={(e) => onChange?.({ dept: e.target.value })} />
              <input className={inputCls} value={judge} onChange={(e) => onChange?.({ judge: e.target.value })} />
            </div>
          )}
        </Row>
        <Row>
          {readOnly ? (
            <span style={{ fontWeight: titleBold ? 700 : 400, fontStyle: titleItalic ? 'italic' as const : 'normal' }}>{title}</span>
          ) : (
            <input
              className={inputCls}
              style={{ fontWeight: titleBold ? 700 : 400, fontStyle: titleItalic ? 'italic' as const : 'normal' }}
              value={title}
              onFocus={onFocusTitle}
              onBlur={onBlurTitle}
              onChange={(e) => onChange?.({ title: e.target.value })}
            />
          )}
        </Row>
        <Row>
          {readOnly ? (
            <>Hearing: {hearingDate} at {hearingTime} – {hearingLocation}</>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              <input className={inputCls} value={hearingDate} onChange={(e) => onChange?.({ hearingDate: e.target.value })} />
              <input className={inputCls} value={hearingTime} onChange={(e) => onChange?.({ hearingTime: e.target.value })} />
              <input className={inputCls} value={hearingLocation} onChange={(e) => onChange?.({ hearingLocation: e.target.value })} />
            </div>
          )}
        </Row>
      </div>
    </div>
  );
};

export default CACaptionComponent;


